import { AfterViewInit, Component, effect, inject, OnInit, signal, TemplateRef, ViewChild } from '@angular/core';
import { select, Store } from '@ngrx/store';
import {
    AlertService,
    ArmyBuilderConfig,
    getLog,
    LoginComponent,
    LoginSelector,
    LogMessage,
    Modal,
    PurchasingService,
    RelativeDatePipe,
    FeatureFlagDirective
} from 'army-builder-shared';
import { IonicModule, ModalController, Platform } from '@ionic/angular';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, combineLatest, distinctUntilChanged, map, switchMap } from 'rxjs';

import { SubscriptionService } from '../subscription/subscription.service';
import { VoucherDetailsComponent } from '../subscription/voucher-details.component';
import { CommonModule } from '@angular/common';
import { WarlordUserService } from '../settings/user/user.service';
import { PaywallComponent } from '../subscription/paywall.component';
import { environment } from 'warlord/src/environments/environment';
import { InAppBrowser } from '@awesome-cordova-plugins/in-app-browser/ngx';
import { ToastService } from 'army-builder-shared/src/lib/global/toast/toast';
import { Router, RouterLink } from '@angular/router';
import { VoucherService } from '../subscription/voucher.service';
import { GameListPage } from './game-list.page';
import { NgLetDirective } from 'ng-let';

@Component({
    standalone: true,
    imports: [
        IonicModule,
        CommonModule,
        VoucherDetailsComponent,
        TranslateModule,
        GameListPage,
        NgLetDirective,
        RouterLink,
        LoginComponent,
        FeatureFlagDirective
    ],
    selector: 'wl-home',
    template: `
        <ng-container
            *ngLet="{
                loginState: login$ | async,
                hasAccess: userHasAccess$ | async,
                loginChecked: loginChecked$ | async,
                loggedInMessage: loggedInMessage$ | async,
                loggedOutMessage: loggedOutMessage$ | async,
                expiry: expiry$ | async,
                expired: expired$ | async,
                platform: platform$ | async
            } as props"
        >
            <ion-toolbar>
                <ion-buttons slot="end">
                    <ion-menu-button *ngIf="props.loginState?.user"></ion-menu-button>
                </ion-buttons>
            </ion-toolbar>
            <div class="scrollWrapper">
                <div class="homeWrapper">
                    <img src="/assets/images/warlord.png" alt="Warlord" class="warlordLogo" (click)="logoClicked()" />

                    <div class="menu logos home">
                        <div *ngIf="props.platform === 'web'" class="native">
                            <a href="https://play.google.com/store/apps/details?id=com.warlordgames.app&gl=GB"
                                ><img src="/assets/images/google-play.png" [alt]="'GLOBAL.HOME.GOOGLE' | translate"
                            /></a>
                            <a href="https://apps.apple.com/us/app/warlord-games-list-builder/id1637349658"
                                ><img src="/assets/images/app-store.png" [alt]="'GLOBAL.HOME.APPLE' | translate"
                            /></a>
                            <ion-button *ngIf="showOfflineLogin" (click)="offlineLogin()">TEST LOGIN</ion-button>
                        </div>

                        @if (props.loginState?.user?.roles?.includes('app_user')) {
                            <ion-card>
                                <ion-card-content [innerHTML]="props.loggedInMessage"></ion-card-content>
                            </ion-card>

                            @if (props.hasAccess) {
                                <wl-game-list></wl-game-list>
                            } @else {
                                <div class="buttons buttons-vertical">
                                    <ion-button (click)="refreshPurchases()">Refresh Subscription</ion-button>
                                    <ion-button (click)="subscribe()">Subscribe</ion-button>
                                </div>
                            }
                        } @else {
                            <ion-card>
                                <p *ngIf="props.loggedOutMessage" style="font-weight:bold">
                                    {{ props.loggedOutMessage }}
                                </p>
                            </ion-card>
                        }

                        @if (!props.loginState?.user?.roles?.includes('app_user')) {
                            <div class="buttons mobile-only">
                                <ion-button [routerLink]="'/login'">
                                    {{ 'GLOBAL.LOGIN.BUTTONS.LOGIN' | translate }}
                                </ion-button>
                            </div>

                            <div class="loginPanel desktop-only">
                                <abs-login [showLogin]="true" [showRegister]="props.platform !== 'ios'" [splitLayout]="true"></abs-login>
                            </div>
                        }

                        <!--
                            Gated on the events feature flag itself, not on merely being logged in.
                            A login check was an approximation of the flag while events went out to
                            a subset of users, but it let every logged-in account see this entry
                            whether or not the flag was on for them. No separate login condition is
                            needed: a restricted flag (requiredRoles/allowedEmails) already reads
                            false for an anonymous visitor, and once the flag is enabled with no
                            restrictions this correctly comes back for everyone.

                            The /events route itself stays deliberately unguarded, so a shared
                            event link keeps working for someone who cannot see this button.

                            Placed last, after login/subscription content: a secondary path, not
                            the page's primary call to action.
                        -->
                        <div class="buttons" *featureFlag="'events'">
                            <ion-button [routerLink]="'/events'">
                                {{ 'GLOBAL.EVENTS.BROWSE_CTA' | translate }}
                            </ion-button>
                        </div>

                        <p class="eula iosOnly">
                            <a (click)="openLink('https://www.apple.com/legal/internet-services/itunes/dev/stdeula/')">EULA</a> |
                            <a (click)="openLink('https://store.warlordgames.com/policies/privacy-policy')">Privacy Policy</a>
                        </p>
                    </div>
                </div>
            </div>
            <ng-template #debugLogTemplate>
                <p>
                    Copy the text below and email it to <a href="mailto:warlord@maloric.com">warlord&#64;maloric.com</a> along with a
                    description of the problem.
                </p>
                <textarea class="debugLog" [readOnly]="true">{{ debugLog | json }}</textarea>
            </ng-template>
        </ng-container>
        <ng-template #voucherAvailableTemplate>
            <voucher-details [showDismissReminder]="true"></voucher-details>
        </ng-template>
    `,
    styles: [
        `
            :host {
                /* background: black url(/assets/images/banner2.webp) 0 0 / cover repeat-x; */
                /*background: #3c3c3c;*/
                /* animation: slide 10000s linear infinite; */
                position: relative;
                height: 100%;
                background-color: #000;
            }

            :host:before {
                content: ' ';
                display: block;
                position: absolute;
                left: 0;
                top: 0;
                width: 100%;
                height: 100%;
                opacity: 0.4;
                background-image: url(/assets/images/banner2.webp);
                background-repeat: repeat-x;
                background-position: -150px 0;
                background-size: auto 100vh;
                background-attachment: fixed;
            }

            .scrollWrapper {
                /* needs a separate wrapper to fix a bug with ios where the logo is rendered too high up the page */
                overflow: auto;
                flex: 1 1 auto;
            }

            .homeWrapper {
                min-height: 100%;
                padding-top: 200px;
                display: flex;
                flex-direction: column;
                position: relative;
                justify-content: space-around;
                min-height: calc(100vh - 84px);
            }

            ion-toolbar {
                --ion-toolbar-background: none;
                --background: none;
                background: none;
                padding-top: var(--ion-safe-area-top, 0);
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
            }

            @keyframes slide {
                from {
                    background-position: 0 0;
                }
                to {
                    background-position: -250000px 0;
                }
            }

            .warlordLogo {
                margin: 0 auto;
                flex: 0 1 auto;
                max-width: 229px;
            }

            .menu {
                flex: 0 0 auto;
                padding: 40px 10px;
                text-align: center;
                display: flex;
                flex-direction: column;
                justify-content: flex-end;
                max-width: 520px;
                width: 100%;
                align-self: center;
            }

            ion-card {
                margin-inline: 2px;
                margin-left: auto;
                margin-right: auto;
            }
            .native {
                display: flex;
                justify-content: center;
            }
            .native a {
                text-decoration: none;
            }
            .native img {
                height: 70px;
            }

            ion-menu-button {
                color: #eee;
            }

            .eula,
            .eula a {
                color: white;
                font-size: 0.85em;
            }

            .debugLog {
                width: 100%;
                height: 100px;
                overflow: auto;
                font-family: monospace;
            }

            @media screen and (min-width: 992px) {
                ion-menu-button {
                    display: none;
                }

                .homeWrapper {
                    padding-top: 50px;
                }

                .menu {
                    max-width: 100%;
                }

                .loginPanel {
                    max-width: 700px;
                    margin: 0 auto;
                    padding: 24px 32px;
                    background: #fff;
                    border-radius: var(--container-radius, 8px);
                }

                .buttons {
                    width: 520px;
                    margin: 0 auto;
                    padding: 0;
                }

                :host:before {
                    background-position: -300px 0;
                    background-size: auto 100vh;
                }
            }
        `
    ]
})
export class HomePage implements AfterViewInit {
    showOfflineLogin = environment.showOfflineLogin;
    login$ = this.store.pipe(select(LoginSelector));
    loginChecked$ = this.userService.loginChecked$;
    userHasAccess$ = this.subscriptionService.userHasAccess$;
    expiry$ = this.subscriptionService.expiry$;
    expired$ = this.expiry$.pipe(map((exp) => exp && exp < new Date()));
    platform$ = new BehaviorSubject('web');

    router = inject(Router);
    purchasingService = inject(PurchasingService);
    alertService = inject(AlertService);
    logoClickCount = 0;

    loggedInMessage$ = combineLatest([this.login$, this.platform$]).pipe(
        switchMap(([l, platform]) => {
            let key = 'GLOBAL.LOGIN.MESSAGE.LOGGED_IN';
            if (platform === 'ios') {
                key += '.IOS';
            } else if (platform === 'android') {
                key += '.ANDROID';
            } else {
                key += '.WEB';
            }

            return this.translateService.get(key, l.user);
        }),
        map((msg) => (msg.startsWith('GLOBAL.LOGIN.MESSAGE') ? '' : msg)),
        distinctUntilChanged()
    );

    loggedOutMessage$ = this.platform$.pipe(
        switchMap((platform) => {
            let key = 'GLOBAL.LOGIN.MESSAGE.LOGGED_OUT';
            if (platform === 'ios') {
                key += '.IOS';
            } else if (platform === 'android') {
                key += '.ANDROID';
            } else {
                key += '.WEB';
            }
            return this.translateService.get(key);
        }),
        map((msg) => (msg.startsWith('GLOBAL.LOGIN.MESSAGE') ? '' : msg)),
        distinctUntilChanged()
    );

    @ViewChild('debugLogTemplate')
    debugLogTemplate: TemplateRef<any>;
    debugLog: LogMessage[] = [];

    @ViewChild('voucherAvailableTemplate')
    voucherAvailableTemplate: TemplateRef<any>;

    viewReady = signal(false);

    constructor(
        public userService: WarlordUserService,
        private subscriptionService: SubscriptionService,
        private store: Store,
        platform: Platform,
        private translateService: TranslateService,
        private modalController: ModalController,
        private modal: Modal,
        protected config: ArmyBuilderConfig,
        private iab: InAppBrowser,
        private toastService: ToastService,
        private voucherService: VoucherService
    ) {
        if (platform.is('ios') && !platform.is('mobileweb')) {
            this.platform$.next('ios');
        } else if (platform.is('android') && !platform.is('mobileweb')) {
            this.platform$.next('android');
        }

        let showReminder = false;
        effect(() => {
            if (!this.viewReady()) {
                return;
            }
            // Check if user can generate a new voucher, and if so, show a modal telling them so
            if (this.voucherService.showReminder()) {
                if (showReminder) {
                    return;
                }
                showReminder = true;
                this.modal.show({
                    props: {
                        contentTemplate: this.voucherAvailableTemplate,
                        // buttonsTemplate?: TemplateRef<any>;
                        cssClass: 'home',
                        title: 'New Voucher Available'
                        // data?: any;
                        // buttonsDirection?: 'vertical' | 'horizontal';
                        // [propName: string]: any;
                    }
                });
            }
        });
    }

    ngAfterViewInit() {
        this.viewReady.set(true);
    }

    async logoClicked() {
        this.logoClickCount += 1;

        if (this.logoClickCount === 10) {
            let log = JSON.stringify(getLog());
            try {
                await navigator.clipboard.writeText(log);
                this.toastService.showToast(
                    'Debug log copied to clipboard',
                    'Paste it into an email to warlord@maloric.com with a description of the problem.',
                    { duration: 50000 }
                );
            } catch (err) {
                this.debugLog = getLog();
                this.modal.show({
                    props: {
                        title: 'Debug Log',
                        contentTemplate: this.debugLogTemplate
                    }
                });
            }
        }
    }

    getMessage(key) {
        return;
    }

    logout() {
        this.userService.logout();
    }

    subscribe() {
        this.modalController
            .create({
                component: PaywallComponent
            })
            .then((m) => m.present());
    }

    offlineLogin() {
        this.userService.login({ token: 'Test' });
    }

    openLink(url: string) {
        this.iab.create(url);
    }

    async refreshPurchases() {
        const result = await this.purchasingService.restorePurchases();
        if (!result.success) {
            await this.alertService.showAlert(
                'Restore Failed',
                'Unable to restore purchases from the store. Please check your connection and try again, or contact support at warlord@maloric.com.'
            );
        }
        this.router.navigateByUrl('/subscription/handle-purchase');
    }
}
